import { TabsList, TabsTrigger } from '@/components/ui/tabs'
import { cn } from '@/lib/utils'

/**
 * Underline-on-active styling, matching the Call detail tabs. `mb-0` clears
 * shadcn's `mb-[-1px]` and the underline sits at `bottom-0` (not `-bottom-px`)
 * so the active state stays inside the wrapper instead of adding a scrollbar.
 * The keyboard focus ring is drawn inside the tab for the same reason: the
 * scrolling strip clips anything outside it, which left only a stray vertical
 * edge of the default outset ring showing.
 */
const TAB_TRIGGER = cn(
  'relative shrink-0 rounded-sm border-0 bg-transparent px-0.5 py-3 text-[13px]',
  'focus-visible:ring-inset focus-visible:ring-offset-0',
  'text-text-body hover:bg-transparent hover:text-text-heading',
  'data-[state=active]:text-text-heading data-[state=active]:mb-0 data-[state=active]:font-medium',
  'data-[state=active]:border-0 data-[state=active]:bg-transparent',
  'data-[state=active]:after:absolute data-[state=active]:after:right-0 data-[state=active]:after:left-0',
  'data-[state=active]:after:bottom-0 data-[state=active]:after:h-0.5 data-[state=active]:after:bg-text-heading'
)

export interface SheetTab {
  value: string
  label: string
  /** Renders as a pill after the label; omit for tabs with nothing to count. */
  count?: number
}

/** A details sheet's tab strip. Render inside a `<Tabs>` root alongside the panels. */
export function SheetTabStrip({ tabs }: { tabs: readonly SheetTab[] }) {
  return (
    <div className="overflow-x-auto">
      <TabsList className="w-max min-w-full justify-start gap-7 bg-transparent">
        {tabs.map(({ value, label, count }) => (
          <TabsTrigger key={value} value={value} className={TAB_TRIGGER}>
            {label}
            {count !== undefined ? (
              <span className="bg-neutral-hover-bg text-muted-foreground ml-1.5 rounded-full px-1.5 text-xs font-normal">
                {count}
              </span>
            ) : null}
          </TabsTrigger>
        ))}
      </TabsList>
    </div>
  )
}
